import { type ChangeEvent, useCallback, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useController } from 'react-hook-form';
import type { ExtractDataInput, InferDataItem, UseTypedInfiniteQuery } from '@interxfi/api-client/utils/typed-infinite-query';
import { useFlatData } from '@interxfi/api-client/utils/use-flat-data';
import { ComboboxInput, type ComboboxInputProps } from '@interxfi/design-system/components/input/combobox-input/combobox-input';
import {
  MultiComboboxInput,
  type MultiComboboxInputEvent,
  type MultiComboboxInputProps,
} from '@interxfi/design-system/components/input/multi-combobox-input/multi-combobox-input';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import type { IxmUserauthV1JoinedRealmAccess, J5ListV1QueryRequest } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { STABLE_EMPTY_OBJECT_REF } from '@interxfi/utilities/object/stable-empty';
import { useSearch } from '@interxfi/utilities/search/debounced-search';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../util/error-handler';
import { buildOption, type DataValueKeyOrGetter, getDataValueKey, type OptionDisplayBuilder } from './util';

export interface BuildDataInputOptions<
  TQueryFn extends UseTypedInfiniteQuery<any, any>,
  TResponseDataKey extends keyof ExtractDataInput.Response<TQueryFn> & string,
  TItemValueKey extends (keyof InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey> & string) | undefined = undefined,
> {
  dataValueKey: DataValueKeyOrGetter<InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>, TItemValueKey>;
  entityName: string;
  inputToEnsureDataQuery?: (selection: string[] | undefined, inputValue: string) => ExtractDataInput.Request<TQueryFn>;
  optionDisplayBuilder?: OptionDisplayBuilder<InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>>;
  responseDataKey: TResponseDataKey;
  searchFields?: NonNullable<ExtractDataInput.Request<TQueryFn>['query']> extends J5ListV1QueryRequest<infer TSearchField>
    ? TSearchField extends undefined
      ? undefined
      : TSearchField[]
    : undefined;
  useInfiniteQuery: TQueryFn;
}

export function createDataCombobox<
  TQueryFn extends UseTypedInfiniteQuery<any, any>,
  TResponseDataKey extends keyof ExtractDataInput.Response<TQueryFn> & string,
  TItemValueKey extends (keyof InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey> & string) | undefined = undefined,
>({
  dataValueKey,
  entityName,
  inputToEnsureDataQuery,
  optionDisplayBuilder,
  responseDataKey,
  searchFields,
  useInfiniteQuery,
}: BuildDataInputOptions<TQueryFn, TResponseDataKey, TItemValueKey>) {
  const safeEntityName = entityName.replace(/\s+/g, ' ').trim();
  const errorHandler = buildDefaultErrorMessageGetter(`Failed to load ${entityName}`, `view ${entityName}`);

  type SharedDataComboboxProps<TValue> = {
    ensureData?: InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[];
    dataSorter?: (
      options: InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[],
    ) => InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[];
    ensureDataQuery?: ExtractDataInput.Request<TQueryFn> | null;
    ensureOptions?: InputDropdownOption[];
    excludeDataKeys?: string[];
    filter?: (item: InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>) => boolean;
    searchFields?: typeof searchFields;
    queryOptions?: ExtractDataInput.Request<TQueryFn> | null; // null means to skip the query
    options?: ExtractDataInput.Options<TQueryFn>;
    value: TValue | undefined;
    withAccess?: IxmUserauthV1JoinedRealmAccess | undefined;
  };

  type DataComboboxProps = Omit<ComboboxInputProps, 'options' | 'pagination'> &
    SharedDataComboboxProps<string> & {
      onDataSelect?: (data: InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey> | undefined) => void;
    };

  type DataMultiComboboxProps = Omit<MultiComboboxInputProps, 'options' | 'pagination'> &
    SharedDataComboboxProps<string[]> & {
      onDataSelect?: (data: InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[] | undefined) => void;
    };

  type DataComboboxWithLensProps = Omit<DataComboboxProps, 'value' | 'name'> & {
    shouldUnregister?: boolean;
    lens: Lens<string>;
  };

  type DataMultiComboboxWithLensProps = Omit<DataMultiComboboxProps, 'value' | 'name'> & {
    shouldUnregister?: boolean;
    lens: Lens<string[]>;
  };

  function useDataComboboxProps<TValue extends string | string[]>({
    dataSorter = (x) => x,
    ensureData,
    ensureDataQuery,
    ensureOptions,
    excludeDataKeys,
    filter,
    options: rqOptions,
    queryOptions,
    searchFields,
    value,
    withAccess,
  }: SharedDataComboboxProps<TValue>) {
    const searchProps = useSearch('');
    const { searchValue } = searchProps;
    const finalEnsureDataQuery = useMemo(
      () => inputToEnsureDataQuery?.(value ? (Array.isArray(value) ? value : [value]) : undefined, searchValue) ?? ensureDataQuery,
      [value, ensureDataQuery, searchValue],
    );
    const { data: ensuredDataFromQuery } = useInfiniteQuery(finalEnsureDataQuery ? finalEnsureDataQuery : undefined, rqOptions, withAccess);
    const flatEnsuredQueryData = useFlatData<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>(
      ensuredDataFromQuery,
      responseDataKey,
    ) as InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[];
    // This is to prevent an issue where for some reason data's coming in for an undefined request. TODO: investigate later.
    const usedFlatEnsuredQueryData = finalEnsureDataQuery ? flatEnsuredQueryData : undefined;

    const finalQueryOptions = useMemo(() => {
      if (queryOptions === null) {
        return null;
      }

      if (searchValue && searchFields?.length) {
        const searchQueries = searchFields.map((field) => ({
          field,
          value: searchValue,
        }));

        return {
          ...queryOptions,
          query: {
            ...(queryOptions?.query || {}),
            searches: [...(queryOptions?.query?.searches || []), ...searchQueries],
          },
        } as ExtractDataInput.Request<TQueryFn>;
      }

      return queryOptions || STABLE_EMPTY_OBJECT_REF;
    }, [queryOptions, searchValue, searchFields]);

    const { data, error, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery(finalQueryOptions, rqOptions, withAccess);
    useErrorHandler(error, errorHandler);
    const flatData = useFlatData<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>(data, responseDataKey) as InferDataItem<
      ExtractDataInput.Response<TQueryFn>,
      TResponseDataKey
    >[];
    const usedFlatData = finalQueryOptions !== null ? flatData : STABLE_EMPTY_ARRAY_REF;
    const options = useMemo(() => {
      const combinedData =
        ensureData || usedFlatEnsuredQueryData ? [...(ensureData || []), ...(usedFlatEnsuredQueryData || []), ...usedFlatData] : usedFlatData;
      const filteredData = dataSorter(filter ? combinedData.filter(filter) : combinedData);

      return filteredData.reduce<InputDropdownOption[]>(
        (acc, item) => {
          const option = buildOption<InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>, TItemValueKey>(
            item,
            dataValueKey,
            optionDisplayBuilder,
          );

          if (!excludeDataKeys?.find((excluded) => excluded === option.value) && !acc.find((o) => o.value === option.value)) {
            acc.push(option);
          }

          return acc;
        },
        [...(ensureOptions || [])],
      );
    }, [ensureData, ensureOptions, usedFlatEnsuredQueryData, excludeDataKeys, filter, usedFlatData, dataSorter]);

    const hasQuery = Boolean(finalQueryOptions);

    return useMemo(
      () => ({
        data: usedFlatData,
        options,
        pagination: { hasNextPage, isFetchingNextPage, fetchNextPage },
        dataValueKey,
        isPending: hasQuery && isPending,
        ...searchProps,
      }),
      [usedFlatData, options, hasNextPage, hasQuery, isPending, isFetchingNextPage, fetchNextPage, searchProps],
    );
  }

  function DataComboboxInput({
    dataSorter,
    onDataSelect,
    onChange,
    ensureData,
    ensureDataQuery,
    ensureOptions,
    excludeDataKeys,
    filter,
    queryOptions,
    options: rqOptions,
    value,
    withAccess,
    ...props
  }: DataComboboxProps) {
    const { data, isPending, options, pagination, realTimeSearchValue, setSearchValue } = useDataComboboxProps<string>({
      dataSorter,
      ensureData,
      ensureDataQuery,
      ensureOptions,
      excludeDataKeys,
      filter,
      queryOptions,
      searchFields,
      options: rqOptions,
      value,
      withAccess,
    });

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        onChange?.(e);
        onDataSelect?.(data?.find((item) => e.target.value === getDataValueKey(item, dataValueKey)));
      },
      [onDataSelect, onChange, data],
    );

    return (
      <ComboboxInput
        {...props}
        hasControlledFilters={(searchFields?.length || 0) > 0}
        inputValue={realTimeSearchValue}
        isLoading={isPending}
        onChange={handleChange}
        onInputValueChange={setSearchValue}
        options={options}
        pagination={pagination}
        value={value}
      />
    );
  }

  DataComboboxInput.displayName = `${safeEntityName}DataComboboxInput`;

  function DataComboboxInputWithLens({ lens, shouldUnregister, onChange, ...props }: DataComboboxWithLensProps) {
    const { field, fieldState } = useController({ shouldUnregister, ...lens.interop() });
    const { onChange: fieldOnChange } = field;
    const handleChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        fieldOnChange(e.target.value);
        onChange?.(e);
      },
      [fieldOnChange, onChange],
    );

    return (
      <DataComboboxInput
        {...props}
        {...field}
        error={fieldState.error?.message ?? props.error}
        hasControlledFilters={(searchFields?.length || 0) > 0}
        onChange={handleChange}
        touched={fieldState.isTouched}
        value={field.value ?? ''}
      />
    );
  }

  DataComboboxInputWithLens.displayName = `${safeEntityName}DataComboboxLensInput`;

  function DataMultiComboboxInput({
    dataSorter,
    onDataSelect,
    onChange,
    value,
    ensureData,
    ensureDataQuery,
    ensureOptions,
    excludeDataKeys,
    filter,
    queryOptions,
    options: rqOptions,
    withAccess,
    ...props
  }: DataMultiComboboxProps) {
    const { data, isPending, options, pagination, realTimeSearchValue, setSearchValue } = useDataComboboxProps<string[]>({
      dataSorter,
      ensureData,
      ensureDataQuery,
      ensureOptions,
      excludeDataKeys,
      filter,
      queryOptions,
      searchFields,
      options: rqOptions,
      value,
      withAccess,
    });

    const handleChange = useCallback(
      (e: MultiComboboxInputEvent) => {
        onChange?.(e);

        const selectedData = (e.target.value || []).reduce<InferDataItem<ExtractDataInput.Response<TQueryFn>, TResponseDataKey>[]>((acc, curr) => {
          const match = data?.find((item) => getDataValueKey(item, dataValueKey) === curr);

          if (match) {
            acc.push(match);
          }

          return acc;
        }, []);

        onDataSelect?.(selectedData);
      },
      [onDataSelect, onChange, data],
    );

    return (
      <MultiComboboxInput
        {...props}
        hasControlledFilters={(searchFields?.length || 0) > 0}
        inputValue={realTimeSearchValue}
        isLoading={isPending}
        onChange={handleChange}
        onInputValueChange={setSearchValue}
        options={options}
        pagination={pagination}
        value={value}
      />
    );
  }

  DataMultiComboboxInput.displayName = `${safeEntityName}DataMultiComboboxInput`;

  function DataMultiComboboxInputWithLens({ lens, shouldUnregister, onChange, ...props }: DataMultiComboboxWithLensProps) {
    const { field, fieldState } = useController({ shouldUnregister, ...lens.interop() });
    const { onChange: fieldOnChange } = field;
    const handleChange = useCallback(
      (e: MultiComboboxInputEvent) => {
        fieldOnChange(e.target.value);
        onChange?.(e);
      },
      [fieldOnChange, onChange],
    );

    return (
      <DataMultiComboboxInput
        {...props}
        {...field}
        error={fieldState.error?.message ?? props.error}
        hasControlledFilters={(searchFields?.length || 0) > 0}
        onChange={handleChange}
        touched={fieldState.isTouched}
        value={field.value ?? STABLE_EMPTY_ARRAY_REF}
      />
    );
  }

  DataMultiComboboxInputWithLens.displayName = `${safeEntityName}DataMultiComboboxLensInput`;

  return { DataComboboxInput, DataComboboxInputWithLens, DataMultiComboboxInput, DataMultiComboboxInputWithLens };
}
